import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";

const GRADE_COLOR = {
  A: "#5C8D73",
  B: "#547A8F",
  C: "#D18E63",
  D: "#C66B59",
};

export default function CanSlimWidget() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total_evaluated: 0, total_in_watchlist: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api
      .get("/canslim/watchlist/top?limit=5")
      .then((r) => {
        if (cancel) return;
        setItems(r.data.items || []);
        setMeta({
          total_evaluated: r.data.total_evaluated || 0,
          total_in_watchlist: r.data.total_in_watchlist || 0,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]"
        data-testid="canslim-widget-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Scoring your watchlist...
      </div>
    );
  }

  if (meta.total_in_watchlist === 0) {
    return null; // no stocks in watchlist → silently hide
  }

  return (
    <div
      className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
      data-testid="canslim-widget"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
            <Award className="w-3 h-3 text-[#D18E63]" strokeWidth={1.75} />
            CAN SLIM Leaders
          </div>
          <div className="font-heading text-xl tracking-tight text-[#2A2F2B]">
            Your top-graded stocks
          </div>
          <div className="text-xs text-[#6B7068] mt-0.5">
            {meta.total_evaluated} of {meta.total_in_watchlist} watchlist stocks scored
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div
          className="text-sm text-[#6B7068] py-6 text-center"
          data-testid="canslim-widget-empty"
        >
          No CAN SLIM scores available yet — try adding well-known US stocks
          (AAPL, NVDA, MSFT…) to your watchlist.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <Link
              key={it.symbol}
              to={`/asset/stock/${it.symbol}`}
              data-testid={`canslim-widget-row-${it.symbol}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F9F8F6] transition-soft border border-transparent hover:border-[#EAE6E1] group"
            >
              <div className="font-mono-ticker text-xs text-[#6B7068] w-5">
                {idx + 1}
              </div>
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-heading text-lg font-bold text-white"
                style={{ backgroundColor: GRADE_COLOR[it.grade] || "#6B7068" }}
              >
                {it.grade}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono-ticker text-sm font-semibold text-[#2A2F2B]">
                  {it.symbol}
                </div>
                <div className="text-xs text-[#6B7068] truncate">{it.name}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-ticker text-sm text-[#2A2F2B]">
                  {it.total}
                  <span className="text-[#6B7068]">/{it.max}</span>
                </div>
                <div className="text-[10px] text-[#6B7068] font-mono-ticker">
                  {it.percent}%
                </div>
              </div>
              <ArrowRight
                className="w-4 h-4 text-[#6B7068] opacity-0 group-hover:opacity-100 transition-soft"
                strokeWidth={1.5}
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
